import { ALL_SCHEDULED_JOBS } from '../failures/jobs';
import { CAPTCHA_SECRET_KEY } from '../leads/intake';
import { cents, formatCurrency } from '../money';
import { OPTIONAL_MODULE_KEYS } from './modules';
import { SEALED_SETTING_KEYS } from './maps';
import { SSO_SETTING_KEYS } from '../identity/sso';
import { ZOHO_BOOKS_SETTING_KEYS } from '../import/zohoBooksSettings';
import { ZOHO_CRM_REFRESH_TOKEN_KEY } from '../import/zohoCrm';
import { ZOHO_EXPENSE_REFRESH_TOKEN_KEY } from '../import/zohoExpense';
import { PRINT_MARGIN_KEYS } from '../print/settings';
import { AUTO_CONVERSION_KEY_PREFIX } from '../documents/settings';
import { t, type StringKey } from '../strings';

/**
 * 🔒 Settings › History (piece 15; decision 2771; T1626): every change a person makes to an Organization setting,
 * newest first, each naming when, who, the setting and the value before and after. Every such write records one
 * `AuditEvent` of action `settings.value.changed` on entity `SettingValue`, and this page reads those rows.
 */
export const SETTINGS_HISTORY_ROUTE = '/settings/system/history';
/** The read's own route under the Settings API, which the page asks. */
export const SETTINGS_HISTORY_PATH = '/settings/history';
/** The page lists the newest this many changes; the Audit Viewer still holds every row. */
export const SETTINGS_HISTORY_LIMIT = 200;

/** One change as the page reads it. */
export interface SettingHistoryRow {
  id: string;
  occurredAt: string;
  actorPersonId: string;
  actorName: string | null;
  key: string;
  /** The Index entry the setting's page answers to, and its title, or null where the map names none. */
  entryRoute: string | null;
  entryTitle: string | null;
  from: unknown;
  to: unknown;
}

/**
 * 🔒 Each Index entry's route and the Organization keys its page edits (decision 2771), so a change names the
 * setting's page and links to it. Kept beside the Index search's words; a test refuses a key the server edits
 * that no entry names. A family of keys built from a list (the job hours, the auto-conversion modes, the print
 * margins, the module switches, single sign-on and Zoho Books) is named once in `settingFamilyEntry` instead.
 * 🔨 An entry here is an Index row's route, never a page the Index does not list: a key on a vendor's step page
 * names the Integrations entry, which is where a person finds it.
 */
export const SETTING_KEY_ENTRIES: Readonly<Record<string, readonly string[]>> = {
  '/mail/settings': ['mail.retentionDays', 'mail.departedMailboxRetentionDays', 'mail.undoSendDelaySeconds', 'mail.moduleSenders'],
  '/settings/accounting/approvals': ['approvals.chainDepth'],
  '/settings/accounting/document-rules': ['documents.staleSendWarningDays', 'rfq.chaseEveryWorkingDays', 'rfq.noResponseAlarmWorkingDays', 'creditNote.nonReturnableLine'],
  '/settings/banking/payment-rules': ['bills.varianceTolerancePercent', 'matching.postingDateWindowDays', 'matching.amountToleranceCents', 'matching.nameSimilarityPercent', 'checks.numberingDirection', 'payments.cardSurchargePercent'],
  '/settings/service/dispatch-rules': ['approvals.retrospectiveWindowHours', 'workorder.inboundReplyWindowHours', 'schedule.noticeQuietMinutes', 'agreements.renewalReminderDays', 'oncall.backupAfterMinutes', 'travel.roundingIncrementMinutes', 'travel.cacheStalenessDays', 'dispatch.defaultOriginSiteId'],
  '/settings/crm/pipeline-rules': ['deals.quickDealApprovalThreshold', 'deals.ownerEscalationWindowHours', 'deals.equipmentPhotoKinds', 'documents.discountLevel', 'followup.absenceGraceDays', 'hygiene.nightlyRunHour', 'leads.conversionOverrideAttempts', 'followup.customerDays', 'followup.prospectDays', 'followup.vendorOnlyDays', 'followup.otherDays'],
  '/settings/hr/hr-rules': ['hr.externalEscalationRoute', 'hr.documentRetentionYears', 'hr.certificationExpiryLeadDays', 'hr.retaliationGuardDays', 'hr.acknowledgementReminderDays', 'hr.awaitingDeliveryStallDays', 'hr.absenceEscalationTimeoutDays', 'hr.substancePolicy.procedure', 'hr.substancePolicy.consequences', 'hr.substancePolicy.testingRule', 'hr.punchRadiusMeters'],
  '/settings/accounting/basis': ['ledger.cashBasisCogsTiming', 'ledger.defaultBasis', 'inventory.costBasis'],
  '/settings/accounting/customer-credit': ['credit.defaultCreditLimit', 'ar.financeChargeEscalationEmploymentId', 'ar.financeChargeRate'],
  '/settings/accounting/documents-inbox': ['accounting.inboxAddress'],
  '/settings/accounting/email-templates': ['mail.legalFooterHtml'],
  '/settings/accounting/expense-categories': ['expenses.mealPerPersonThreshold', 'expenses.mileageRatePerMile'],
  '/settings/accounting/form-1099-threshold': ['tax.form1099ThresholdAmount'],
  '/settings/accounting/labour-and-skill-levels': ['pricing.laborModifierComposition', 'pricing.afterHoursModifier'],
  '/settings/catalog/import-price-list': ['catalog.priceListPresets'],
  '/settings/catalog/pricing-defaults': ['pricing.priceStalenessDays', 'pricing.costProjectionRatePercent'],
  '/settings/company/organisation': ['display.dateFormat', 'documents.displayedQuantityPrecision', 'display.tileListRows'],
  '/settings/crm/territories': ['checkin.coordinateRetentionDays', 'checkin.coordinateToleranceMeters'],
  '/settings/crm/website-lead-routing': ['leadintake.originAllowlist', 'leadintake.dispatchDepartmentId', 'leadintake.fieldByteCap', 'leadintake.payloadByteCap', 'leadintake.perIpPerHour', 'leadintake.globalPerHour', 'leadintake.minimumFillSeconds', 'leadintake.dedupWindowHours', 'leadintake.captchaSiteKey', 'leadintake.captchaSecret', 'leadintake.ruleReleaseRateThreshold'],
  '/settings/accounting/photo-processing': ['files.photoBlurThreshold'],
  '/settings/documents/file-types': ['files.blockedExtensions'],
  '/settings/documents/reading': ['ocr.googleApiKey'],
  '/settings/documents/release': ['files.shareLinkExpiryDays', 'files.releaseDefaultPermission', 'files.releaseDownloadOffered', 'files.releaseWatermarkText', 'files.releaseApprovalRuleId'],
  '/settings/documents/storage': ['backup.enabled', 'backup.mirrorRunHour', 'backup.databaseDumpRunHour', 'backup.dumpRetentionDays', 'backup.recoverPrepareHours', 'backup.recoverKeepDays', 'backup.recoverStartTimeoutSeconds'],
  '/settings/integrations': ['files.malwareScannerQuietHours', 'files.malwareSignatureMaxAgeDays', 'travel.bufferPaddingMinutes', 'maps.provider', 'maps.googleApiKey', 'maps.uspsConsumerKey', 'maps.uspsConsumerSecret', 'maps.geoapifyApiKey', 'maps.geoapifyDailyLimit', 'address.agreementCount', 'bankFeed.provider', 'booksSource.provider', 'malwareScan.provider', 'files.malwareScannerAddress', 'payments.stripeSecretKey', 'payments.stripeWebhookSecret', 'payments.stripePublishableKey', 'banking.plaidClientId', 'banking.plaidSecret', 'banking.plaidEnvironment', 'ai.provider', 'ai.anthropicApiKey', 'ai.model', 'ai.inputCentsPerMillionTokens', 'ai.outputCentsPerMillionTokens', 'ai.webSearchCentsPerSearch', 'providerCalls.monthlyCapCents', 'enrichment.provider', 'enrichment.peopleDataApiKey', 'enrichment.peopleDataCentsPerLookup', 'providerCalls.peopleDataMonthlyCallCap', 'communications.twilioAccountSid', 'communications.twilioAuthToken', 'communications.twilioFromNumber', 'communications.voicemailGreeting', 'communications.menuWords', 'communications.businessHours', 'communications.ringSeconds', 'communications.repFirstSeconds', 'communications.twilioApiKeySid', 'communications.twilioApiKeySecret', 'communications.twilioTwimlAppSid', 'communications.twilioPushCredentialIos', 'communications.twilioPushCredentialAndroid'],
  '/settings/knowledge/quiz-flag': ['kb.questionFailureThresholdPercent', 'kb.questionFailureMinimumAttempts'],
  '/settings/notifications': ['approvals.reminderIntervalDays'],
  '/settings/people/logins': ['auth.lockoutThreshold', 'auth.lockoutWindowMinutes', 'auth.resetLinkMinutes', 'auth.resetRequestsPerHour', 'auth.trustedDeviceDays', 'attendance.clockLockoutThreshold', 'attendance.clockLockoutWindowMinutes'],
  // Piece 35 (T2466): the session days moved here from Security when Settings › Mobile shipped (piece 15).
  '/settings/mobile': ['mobile.offlineStoreExpiryDays', 'mobile.syncWarningDays', 'mobile.deviceSessionDays', 'mobile.foregroundSyncSeconds', 'mobile.minimumSupportedBuild'],
  '/settings/security': ['auth.sessionLifetimeMinutes', 'auth.sessionIdleMinutes', 'security.alertEmail'],
  '/settings/system/developer-routes': ['system.developerRoutesEnabled'],
  // 🔒 Decision 5631: the sweep's nightly limit sits beside the job's hour on the Scheduled jobs page.
  '/settings/system/scheduled-jobs': ['recordChecks.sweepLimit'],
  '/settings/tax/sales-tax': ['tax.hiddenForms', 'tax.nexusWarningPercent'],
  // 🔒 Decision 9600: the organization's own exemption facts a Purchase Order's one-time form prints.
  '/settings/tax/our-exemption': ['tax.exemptionNumbers', 'tax.exemptionSignerName', 'tax.exemptionSignerTitle', 'tax.exemptionReason'],
};

/** The Index entry for a family of keys some list builds, or null for a key no family holds. */
function settingFamilyEntry(key: string): string | null {
  if (ALL_SCHEDULED_JOBS.some((job) => job.hourKey === key)) return '/settings/system/scheduled-jobs';
  if (key.startsWith(AUTO_CONVERSION_KEY_PREFIX)) return '/settings/accounting/document-rules';
  if ((PRINT_MARGIN_KEYS as readonly string[]).includes(key)) return '/settings/documents/print-templates';
  if ((OPTIONAL_MODULE_KEYS as readonly string[]).includes(key)) return '/settings/modules';
  // 🔒 Decision 8380: the CRM's token reads on History under Integrations, where the Zoho client it shares stands.
  if (SSO_SETTING_KEYS.includes(key) || (ZOHO_BOOKS_SETTING_KEYS as readonly string[]).includes(key) || key === ZOHO_CRM_REFRESH_TOKEN_KEY || key === ZOHO_EXPENSE_REFRESH_TOKEN_KEY) return '/settings/integrations';
  return null;
}

let entryByKey: Map<string, string> | null = null;

/** The Index entry route a setting's page answers to, or null where neither the map nor a family names one. */
export function settingEntryRoute(key: string): string | null {
  if (entryByKey === null) {
    entryByKey = new Map();
    for (const [route, keys] of Object.entries(SETTING_KEY_ENTRIES)) for (const each of keys) entryByKey.set(each, route);
  }
  return entryByKey.get(key) ?? settingFamilyEntry(key);
}

/** 🔒 The field's own label on its page, where the page names the field (decision 2771); the entry's title stands alone otherwise. */
export const SETTING_KEY_LABELS: Readonly<Record<string, StringKey>> = {
  'auth.sessionLifetimeMinutes': 'settings.security.sessionLifetime',
  'auth.sessionIdleMinutes': 'settings.security.sessionIdle',
  'mobile.deviceSessionDays': 'settings.security.deviceSessionDays',
  'mobile.offlineStoreExpiryDays': 'settings.mobile.offlineStoreExpiryDays',
  'mobile.syncWarningDays': 'settings.mobile.syncWarningDays',
  'mobile.foregroundSyncSeconds': 'settings.mobile.foregroundSyncSeconds',
  'mobile.minimumSupportedBuild': 'settings.mobile.minimumSupportedBuild',
  'security.alertEmail': 'settings.security.alertEmail',
  'recordChecks.sweepLimit': 'jobs.recordChecksSweep.limit',
  'checkin.coordinateRetentionDays': 'settings.security.coordinateRetention',
  'files.shareLinkExpiryDays': 'settings.security.shareLinkExpiry',
  'checkin.coordinateToleranceMeters': 'settings.security.coordinateTolerance',
  'files.blockedExtensions': 'settings.blocked.list',
  'files.releaseDefaultPermission': 'settings.release.permission',
  'files.releaseDownloadOffered': 'settings.release.downloadOffered',
  'files.releaseWatermarkText': 'settings.release.watermark',
  'files.releaseApprovalRuleId': 'settings.release.approvalRule',
  'mail.legalFooterHtml': 'settings.email.footer',
  'hr.documentRetentionYears': 'settings.hr.retention.field',
  'approvals.chainDepth': 'settings.approvals.chainDepth',
  'approvals.reminderIntervalDays': 'settings.notifications.approvalDays',
  'deals.quickDealApprovalThreshold': 'settings.deals.threshold',
  'deals.ownerEscalationWindowHours': 'settings.deals.escalationWindow',
  'deals.equipmentPhotoKinds': 'settings.deals.photoKinds',
  'documents.discountLevel': 'settings.deals.discountScope',
  'pricing.priceStalenessDays': 'settings.deals.stalenessDays',
  'pricing.costProjectionRatePercent': 'settings.deals.projectionRate',
  'followup.absenceGraceDays': 'settings.leads.graceDays',
  'hygiene.nightlyRunHour': 'settings.leads.runHour',
  'leads.conversionOverrideAttempts': 'settings.leads.overrideAttempts',
  'leadintake.originAllowlist': 'settings.leads.allowlist',
  'leadintake.dispatchDepartmentId': 'settings.leads.dispatchDepartment',
  'leadintake.fieldByteCap': 'settings.leads.fieldCap',
  'leadintake.payloadByteCap': 'settings.leads.payloadCap',
  'leadintake.perIpPerHour': 'settings.leads.perIp',
  'leadintake.globalPerHour': 'settings.leads.global',
  'leadintake.minimumFillSeconds': 'settings.leads.minimumFill',
  'leadintake.dedupWindowHours': 'settings.leads.dedupWindow',
  'leadintake.captchaSiteKey': 'settings.leads.captchaSite',
  'leadintake.captchaSecret': 'settings.leads.captchaSecret',
  'leadintake.ruleReleaseRateThreshold': 'settings.leads.releaseRate',
  'auth.lockoutThreshold': 'settings.logins.lockoutThreshold',
  'auth.lockoutWindowMinutes': 'settings.logins.lockoutWindow',
  'auth.resetLinkMinutes': 'settings.logins.resetLinkMinutes',
  'auth.resetRequestsPerHour': 'settings.logins.resetRequestsPerHour',
  'auth.trustedDeviceDays': 'settings.logins.trustedDeviceDays',
  'attendance.clockLockoutThreshold': 'settings.logins.clockLockoutThreshold',
  'attendance.clockLockoutWindowMinutes': 'settings.logins.clockLockoutWindow',
  'documents.displayedQuantityPrecision': 'settings.precision.decimals',
  'credit.defaultCreditLimit': 'settings.creditLimit.amount',
  'bills.varianceTolerancePercent': 'settings.billVariance.percent',
  'files.photoBlurThreshold': 'settings.photo.blur',
  'files.malwareScannerQuietHours': 'scanner.quietHours',
  'files.malwareSignatureMaxAgeDays': 'scanner.maxAgeDays',
  'system.developerRoutesEnabled': 'settings.developerRoutes.enabled',
  'matching.postingDateWindowDays': 'settings.matchTiers.windowDays',
  'matching.amountToleranceCents': 'settings.matchTiers.toleranceCents',
  'matching.nameSimilarityPercent': 'settings.matchTiers.similarityPercent',
  'expenses.mealPerPersonThreshold': 'settings.expenseCategories.mealThreshold',
  'pricing.laborModifierComposition': 'settings.labour.composition',
  'pricing.afterHoursModifier': 'settings.labour.afterHours',
  'travel.roundingIncrementMinutes': 'settings.travel.rounding',
  'travel.cacheStalenessDays': 'settings.travel.staleness',
  'dispatch.defaultOriginSiteId': 'settings.travel.origin',
  'rfq.chaseEveryWorkingDays': 'settings.accounting.chaseEvery',
  'rfq.noResponseAlarmWorkingDays': 'settings.accounting.alarmAfter',
  'documents.staleSendWarningDays': 'settings.staleSend.days',
  'approvals.retrospectiveWindowHours': 'emergencyApprovals.window',
  'workorder.inboundReplyWindowHours': 'emergencyApprovals.replyWindow',
  'schedule.noticeQuietMinutes': 'scheduleNotices.minutes',
  'agreements.renewalReminderDays': 'agreementRenewals.days',
  'oncall.backupAfterMinutes': 'onCallSettings.minutes',
  'kb.questionFailureThresholdPercent': 'kb.flag.threshold',
  'kb.questionFailureMinimumAttempts': 'kb.flag.minimum',
  'hr.certificationExpiryLeadDays': 'settings.hr.days.certificationLead',
  'hr.retaliationGuardDays': 'settings.hr.days.retaliationGuard',
  'hr.acknowledgementReminderDays': 'settings.hr.days.acknowledgementReminder',
  'hr.awaitingDeliveryStallDays': 'settings.hr.days.awaitingDeliveryStall',
  'hr.absenceEscalationTimeoutDays': 'settings.hr.days.absenceEscalation',
  'hr.externalEscalationRoute': 'settings.hr.route.field',
  'hr.substancePolicy.procedure': 'settings.hr.substance.procedure',
  'hr.substancePolicy.consequences': 'settings.hr.substance.consequences',
  'hr.substancePolicy.testingRule': 'settings.hr.substance.testingRule',
  'hr.punchRadiusMeters': 'settings.hr.punch.field',
};

export function settingKeyLabel(key: string): StringKey | null {
  return SETTING_KEY_LABELS[key] ?? null;
}

/** 🔒 The keys whose value is held in whole cents, drawn as dollars through the one money formatter (decision 0564). */
export const SETTING_MONEY_KEYS: readonly string[] = [
  'credit.defaultCreditLimit', 'expenses.mealPerPersonThreshold', 'matching.amountToleranceCents', 'tax.form1099ThresholdAmount', 'expenses.mileageRatePerMile', 'providerCalls.monthlyCapCents',
];

/**
 * 🔒 The keys whose value the audit trail records as held and never as itself (decision 2771): every sealed key, and
 * the lead form's CAPTCHA secret, which the store keeps in plain text but a reader of the history has no need of.
 */
export const HIDDEN_HISTORY_KEYS: readonly string[] = [...SEALED_SETTING_KEYS, CAPTCHA_SECRET_KEY];

/** What a hidden value is recorded as: that one is held, never what it is. */
export const HELD_VALUE = { held: true } as const;

/** The value an audit row records for a key: a hidden key's present value is `HELD_VALUE`, an absent one null. */
export function auditedSettingValue(key: string, value: unknown): unknown {
  if (value === undefined || value === null || value === '') return null;
  return HIDDEN_HISTORY_KEYS.includes(key) ? HELD_VALUE : value;
}

/** A value as the page draws it: a string key for a word the page says, or the value's own text. */
export type SettingHistoryText = { word: StringKey } | { text: string };

function isHeld(value: unknown): boolean {
  return typeof value === 'object' && value !== null && (value as { held?: unknown; sealed?: unknown }).held === true;
}

function plain(key: string, value: unknown): string {
  if (typeof value === 'number' && SETTING_MONEY_KEYS.includes(key) && Number.isInteger(value)) return formatCurrency(cents(value));
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map((each) => plain(key, each)).join(', ');
  if (typeof value === 'object' && value !== null) {
    const dated = value as { effectiveFrom?: unknown; value?: unknown };
    if (typeof dated.effectiveFrom === 'string' && 'value' in dated) return `${plain(key, dated.value)} ${t('settings.history.from')} ${dated.effectiveFrom}`;
    return Object.entries(value).map(([name, each]) => `${name}: ${plain(key, each)}`).join(', ');
  }
  return String(value);
}

/**
 * 🔒 A value drawn in words (decision 2771): a switch reads On or Off, an absent value Not Set, a held one Hidden,
 * and an amount in cents as dollars. A dated series, which older rows recorded as its JSON text, reads as each
 * value with the day it took effect.
 */
export function settingHistoryText(key: string, value: unknown): SettingHistoryText {
  if (value === undefined || value === null || value === '') return { word: 'settings.history.notSet' };
  if (isHeld(value) || HIDDEN_HISTORY_KEYS.includes(key)) return { word: 'settings.history.hidden' };
  if (value === true) return { word: 'settings.history.on' };
  if (value === false) return { word: 'settings.history.off' };
  if (typeof value === 'string' && /^\[\s*\{/.test(value)) {
    try {
      return { text: plain(key, JSON.parse(value) as unknown) };
    } catch {
      return { text: value };
    }
  }
  return { text: plain(key, value) };
}
